import {Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {DictionaryState} from '../../../../state/dictionary/dictionary.state';
import {Subscription} from 'rxjs';
import {Store} from '@ngxs/store';
import {TranslateService} from '@ngx-translate/core';
import {CustomFormControl} from '../custom-form-control';
import {DictionaryCode} from '../../../../enums/dictionary-code.enum';
import {EMPTY_FUNCTION} from '../../../../helpers/empty.helper';

const getBooleanOptions = () => {
    return [
        {value: true, description: 'LABELS.YES'},
        {value: false, description: 'LABELS.NO'}
    ];
};

@Component({
    selector: 'app-select',
    templateUrl: './select.component.html',
    styleUrls: ['./select.component.less'],
    providers: [
        {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SelectComponent), multi: true},
    ]
})
export class SelectComponent extends CustomFormControl implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
    @Input() items: any[] = [];
    @Input() sortItemsBy: string;
    @Input() dictionaryCode: DictionaryCode;
    @Input() dictionaryFilter: string;
    @Input() bindBy: string;
    @Input() bindLabel: string;
    @Input() bindValue: string;
    @Input() placeholder = 'LABELS.SELECT_PROMPT';
    @Input() size: string;
    @Input() boolean = false;
    @Input() clearable = true;
    @Input() searchable = false;
    @Input() multiple = false;
    @Input() textOnDisabled = false;
    @Input() optionDisable: (option) => boolean;
    @Input() virtualSize = 8;
    @Output() open: EventEmitter<any> = new EventEmitter<any>();
    @Output() close: EventEmitter<any> = new EventEmitter<any>();
    subscriptions: Subscription = new Subscription();

    DROPDOWN_CLASS = 'app-select-dropdown';

    _onChange: (value: any) => void = EMPTY_FUNCTION;
    _onTouched = EMPTY_FUNCTION;

    constructor(
        private store: Store,
        private translateService: TranslateService
    ) {
        super();
    }

    ngOnInit(): void {
        if (this.boolean) {
            this.items = getBooleanOptions();
            this.bindLabel = 'description';
            this.bindValue = 'value';
        }
        if (this.control) {
            this.subscriptions.add(
                this.control.valueChanges.subscribe(value => {
                    this._onChange(value);
                })
            );
        }

        if (this.items && this.sortItemsBy) {
            this.items = this.sortBy(this.items, this.sortItemsBy);
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (this.control && !Array.isArray(this.control.value) && this.multiple) {
            this.control.setValue(null);
        }
        if (!Array.isArray(this.items)) {
            this.items = [];
        }
        if (this.dictionaryCode) {
            this.handleDictionaryCode();
        }
        if (changes?.dictionaryFilter && !changes?.dictionaryFilter?.firstChange) {
            this.handleDictionaryFilter();
        }
    }

    handleDictionaryCode() {
        const dictionary = this.dictionaryFilter
            ? this.store.selectSnapshot(DictionaryState.getFilteredDictionary(this.dictionaryCode, this.dictionaryFilter))
            : this.store.selectSnapshot(DictionaryState.getDictionary(this.dictionaryCode));
        this.items = dictionary?.itemList || [];

        if (this.sortItemsBy) {
            this.items = this.sortBy(this.items, this.sortItemsBy);
        }
        if (!this.bindLabel) {
            this.bindLabel = 'description';
        }
        if (!this.bindBy) {
            this.bindBy = 'code';
        }
    }

    handleDictionaryFilter() {
        if (
            !this.multiple && !this.getSelectedItem()
            || this.multiple
        ) {
            this.writeValue(undefined);
        }
        if (this.items?.length === 1) {
            this.writeValue(this.items?.[0]);
        }
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }


    writeValue(value) {
        this.control.setValue(value);
    }

    registerOnChange(fn: () => {}): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: () => {}): void {
        this._onTouched = fn;
    }

    compareWith = (o1: any, o2: any) => {
        if (o1 === undefined && o2 === undefined) {
            return false;
        }
        if (this.bindBy && !this.bindValue) {
            const value1 = o1 ? o1[this.bindBy] : undefined;
            const value2 = o2 ? o2[this.bindBy] : undefined;
            return value1 !== undefined && value2 !== undefined && value1 === value2;
        }
        return o1 === o2;
    };

    getSelectedLabels() {
        if (this.control.value === undefined || this.control.value === null || !Array.isArray(this.items)) {
            return '-';
        }
        // handle single
        if (!this.multiple) {
            const selectedItem = this.getSelectedItem();
            return selectedItem
                ? this.bindLabel
                    ? selectedItem[this.bindLabel]
                    : selectedItem
                : '-';
        }

        // handle multiple
        if (!Array.isArray(this.control.value)) {
            return '-';
        }
        const selectedItems = this.getSelectedItems();
        return this.bindLabel
            ? selectedItems.map(item => item[this.bindLabel]).join(', ')
            : selectedItems.join(', ');
    }

    getLabel(item) {
        const label = (this.bindLabel ? item[this.bindLabel] : item);

        if (typeof label === 'number') {
            return item;
        }
        const translatedLabel = this.translateService.instant(label);
        return typeof translatedLabel === 'string'
            ? translatedLabel
            : label;
    }

    handleOpenChange(open: boolean) {
        if (!open) {
            this.close.emit(this.control.value);

            // fix - when NzNotificationService displays message select dropdown was hidden only on hover instead of click-away
            Array.from(document.getElementsByClassName(this.DROPDOWN_CLASS)).forEach(dropdown => {
                while (dropdown.firstChild) {
                    dropdown.firstChild.remove();
                }
            });
        } else {
            this.open.emit(this.control.value);
        }
    }

    sortBy(arr: any[], value: string = null): any[] {
        return arr.slice().sort((a, b) => (a[value] > b[value]) ? 1 : -1);
    }

    getSelectedItem() {
        return this.items.find(item => {
            return this.bindValue
                ? this.control.value === item[this.bindValue]
                : this.bindBy
                    ? this.control?.value?.[this.bindBy] === item?.[this.bindBy]
                    : this.control?.value === item;
        });
    }

    getSelectedItems() {
        return this.items.filter(item => {
            return this.bindValue
                ? this.control.value.includes(item[this.bindValue])
                : this.bindBy
                    ? this.control.value.map(value => value[this.bindBy]).includes(item[this.bindBy])
                    : this.control.value.includes(item);
        });
    }
}
